import keys from 'constants/keys'
import { textToEditorContent } from 'components/CommonComponents/RichTextEditor'
import { AISubtaskType, IAIGeneratedSubtask } from './generation'

/**
 * formData slices the reused AI steps read and write. Neither is part of `TaskFormData`, so every
 * access goes through `as any` — same convention as the AI flow's own `AI_PROMPT` slice.
 *
 * - `AI_PROMPT`        — shared verbatim with `AIPromptStep` (it reads this key itself)
 * - `AI_SUBTASK_DRAFT` — scratch slice for `AISubtaskStep` (its `draftKey` prop), so the step can never
 *                        overwrite the real task and its list of subtasks
 */
export const AI_PROMPT_SLICE = 'AI_PROMPT'
export const AI_SUBTASK_DRAFT_SLICE = 'AI_SUBTASK_DRAFT'

export interface IAiPromptDraft {
  prompt: string
  reference_images: string[]
}

/** Current content of the subtask being enhanced — what the AI steps start from. */
export interface IAiSubtaskSeed {
  prompt?: string
  referenceImages?: string[]
  /** Subtask title. Pass `undefined` for an untouched default so the widget shows its own placeholder. */
  title?: string
  /** TipTap JSON of the description. */
  richText?: any
  images?: string[]
}

/** Shapes an LLM result the way `AISubtaskStep` expects it: one subtask under `sub_tasks[0]`. */
export const buildAiSubtaskDraft = (result: IAIGeneratedSubtask, referenceImages: string[]) => ({
  title: '',
  sub_tasks: [
    {
      // VM maps the LLM title onto the SUBTASK title (what the executor is asked to photograph).
      // The task title is owned by the task-level form and is never touched by AI.
      title: result.title || '',
      type: keys.SUBTASK_TYPES.IMAGE_SUBTASK,
      order_index: 0,
      is_required: true,
      content: {
        rich_text: result.desc ? textToEditorContent(result.desc) : null,
        images: referenceImages,
      },
    },
  ],
})

/** A full AI audit owns the task title; a VM enhancement only owns one subtask title. */
export function buildAiTaskDraft(result: IAIGeneratedSubtask, referenceImages: string[], type: AISubtaskType) {
  const draft = buildAiSubtaskDraft(result, referenceImages)
  return {
    title: result.title || '',
    sub_tasks: [
      {
        ...draft.sub_tasks[0],
        title: type === 'question' ? result.question || '' : '',
        type: type === 'question' ? keys.SUBTASK_TYPES.OPEN_QUESTION : keys.SUBTASK_TYPES.IMAGE_SUBTASK,
      },
    ],
  }
}
